import React, { useEffect, useState } from 'react';
import { Typography, Box, Alert, Divider, CircularProgress } from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import { useSelector, useDispatch } from 'react-redux';
import { sub } from 'date-fns';
import GameLeagueSelector from '../components/GameLeagueSelector';
import ExchangeRatesTable from '../components/market/ExchangeRatesTable';
import CandleChartModal from '../components/market/CandleChartModal';
import { selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';
import {
  fetchLatestRates,
  fetchHistoricalRates,
  resetMarketDataThunk,
  selectLatestRates,
  selectHistoricalRates,
  selectCurrencyIds,
  selectLoadingState,
  selectErrorState,
  clearSelection,
} from '../store/slices/marketAnalysisSlice';
import type { AppDispatch } from '../store/store';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const MarketAnalysisPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // Get data from market analysis slice
  const latestRates = useSelector(selectLatestRates);
  const historicalRates = useSelector(selectHistoricalRates);
  const { exaltedOrbId, chaosOrbId, divineOrbId } = useSelector(selectCurrencyIds);
  const {
    latestRates: isLoadingLatest,
    historicalRates: isLoadingHistorical,
    resettingMarketData: isResetting,
  } = useSelector(selectLoadingState);
  const { latestRates: latestRatesError, historicalRates: historicalRatesError } = useSelector(selectErrorState);

  // Local state for selected item/currency
  const [PriceHistoryModalOpen, setPriceHistoryModalOpen] = useState(false);
  const [_selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [_selectedCurrencyId, setSelectedCurrencyId] = useState<string | null>(null);
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const [selectedCurrencyName, setSelectedCurrencyName] = useState<string>('');

  // Fetch latest exchange rates when game/league selection changes
  useEffect(() => {
    if (selectedGame && selectedLeague) {
      dispatch(
        fetchLatestRates({
          gameId: selectedGame,
          leagueId: selectedLeague,
        }),
      );
    }
  }, [dispatch, selectedGame, selectedLeague]);

  // Handlers for game and league changes
  const handleGameChange = (_gameCode: string) => {
    // Dispatch is handled by the GameLeagueSelector component
    // We'll just make sure to clear any historical selection
    if (PriceHistoryModalOpen) {
      setPriceHistoryModalOpen(false);
      dispatch(clearSelection());
    }
  };

  const handleLeagueChange = (_leagueCode: string) => {
    // Dispatch is handled by the GameLeagueSelector component
    // We'll just make sure to clear any historical selection
    if (PriceHistoryModalOpen) {
      setPriceHistoryModalOpen(false);
      dispatch(clearSelection());
    }
  };

  // Handler for exchange rate click
  const handleExchangeRateClick = (itemId: string, currencyId: string) => {
    // Find the item object for the main item being analyzed (itemId)
    // It could be itemFrom or itemTo in the rates data, so check both.
    let itemForAnalysis: { item_name: string } | undefined;
    for (const rate of latestRates) {
      if (rate.itemFromId === itemId && rate.itemFrom?.item_name) {
        itemForAnalysis = rate.itemFrom;
        break;
      }
      // Check if the itemId might correspond to an item listed as itemTo
      if (rate.itemToId === itemId && rate.itemTo?.item_name) {
        itemForAnalysis = rate.itemTo;
        break;
      }
    }

    // Find the item object for the currency (currencyId)
    let currencyItem: { item_name: string } | undefined;
    for (const rate of latestRates) {
      if (rate.itemFromId === currencyId && rate.itemFrom?.item_name) {
        currencyItem = rate.itemFrom;
        break;
      }
      if (rate.itemToId === currencyId && rate.itemTo?.item_name) {
        currencyItem = rate.itemTo;
        break;
      }
    }

    const resolvedSelectedItemName = itemForAnalysis?.item_name;
    const resolvedCurrencyName = currencyItem?.item_name;

    if (resolvedSelectedItemName && resolvedCurrencyName && selectedGame && selectedLeague) {
      setSelectedItemId(itemId); // Internal state for ID
      setSelectedCurrencyId(currencyId); // Internal state for ID
      setSelectedItemName(resolvedSelectedItemName); // State for modal prop
      setSelectedCurrencyName(resolvedCurrencyName); // State for modal prop

      // Calculate a default time range (90 days ago)
      const startDate = sub(new Date(), { days: 90 }).toISOString();

      // Fetch historical data
      dispatch(
        fetchHistoricalRates({
          itemId: itemId,
          currencyId: currencyId,
          gameId: selectedGame,
          leagueId: selectedLeague, // This uses the current selectedLeague from Redux state
          startDate: startDate,
        }),
      );

      // Open the modal
      setPriceHistoryModalOpen(true);
    } else {
      console.error('MarketAnalysisPage: Could not resolve item/currency names, or game/league not selected.', {
        itemId,
        currencyId,
        resolvedSelectedItemName,
        resolvedCurrencyName,
        selectedGame,
        selectedLeague,
        itemExistsInLatestRates: latestRates.some(r => r.itemFromId === itemId || r.itemToId === itemId),
        currencyExistsInLatestRates: latestRates.some(r => r.itemFromId === currencyId || r.itemToId === currencyId),
      });
      // Optionally, inform the user via a toast or alert that data couldn't be displayed.
    }
  };

  // Handler for closing the history modal
  const handleClosePriceHistoryModal = () => {
    setPriceHistoryModalOpen(false);
  };

  // Handler for market data reset
  const handleResetMarketData = () => {
    if (selectedGame && selectedLeague) {
      dispatch(
        resetMarketDataThunk({
          gameId: selectedGame,
          leagueId: selectedLeague,
        }),
      ).then(() => {
        // Refresh the data after reset
        dispatch(
          fetchLatestRates({
            gameId: selectedGame,
            leagueId: selectedLeague,
          }),
        );
      });
    }
  };

  return (
    <PageLayout>
      <PageHeader icon={<TrendingUpIcon />} title='Market Analysis' subtitle='Real-time market data and price trends' />

      <Alert
        severity='warning'
        sx={{
          mb: 4,
          fontSize: '1.15rem',
          fontWeight: 500,
          background: '#fffbe6',
          color: '#ad6800',
          border: '2px solid #ffe58f',
        }}
      >
        ⚠️ Item prices, <b>especially for unique items</b>, and <b>ESPECIALLY in PoE2</b>, are not reliable and should not be taken at face
        value at the moment.
        <br />
        Once PoE2 enables the stashing API and we extract Currency Exchange information through the bots, prices will become more accurate.
      </Alert>

      <ContentSection>
        <GameLeagueSelector onGameChange={handleGameChange} onLeagueChange={handleLeagueChange} />
      </ContentSection>

      {/* Error Alerts */}
      {latestRatesError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Error loading exchange rates: {latestRatesError}
        </Alert>
      )}

      {historicalRatesError && PriceHistoryModalOpen && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Error loading historical data: {historicalRatesError}
        </Alert>
      )}

      <ContentSection title='Current Exchange Rates'>
        <EnhancedCard sx={{ p: 3 }}>
          <Typography variant='body2' color='textSecondary' paragraph>
            Click on any exchange rate to view historical price trends and technical analysis.
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {isLoadingLatest && latestRates.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ExchangeRatesTable
              exchangeRates={latestRates}
              exaltedOrbId={exaltedOrbId}
              chaosOrbId={chaosOrbId}
              divineOrbId={divineOrbId}
              isLoading={isLoadingLatest}
              onExchangeRateClick={handleExchangeRateClick}
              onResetMarketData={handleResetMarketData}
              isResetting={isResetting}
              gameId={selectedGame}
              leagueId={selectedLeague}
            />
          )}
        </EnhancedCard>
      </ContentSection>

      {/* Candlestick Chart Modal */}
      <CandleChartModal
        open={PriceHistoryModalOpen}
        onClose={handleClosePriceHistoryModal}
        historicalRates={historicalRates}
        isLoading={isLoadingHistorical}
        selectedItemName={selectedItemName}
        selectedCurrencyName={selectedCurrencyName}
        leagueName={selectedLeague} // Pass the league name
      />
    </PageLayout>
  );
};

export default MarketAnalysisPage;
